import Scene from 'scenejs';


function getTransitionTime(objectSpeed, objectDuration) {
  const speed    = parseFloat(objectSpeed);
  const duration = parseFloat(objectDuration);
  if (!duration || Number.isNaN(duration)) {
    return 0.8;
  }
  let percentage = 12;
  if (Number.isNaN(speed) || speed <= 20) {
    percentage = 80;
  } else if (speed <= 40) {
    percentage = 55;
  } else if (speed <= 60) {
    percentage = 35;
  } else if (speed <= 80) {
    percentage = 20;
  } else {
    percentage = 8;
  }
  return (duration / 100) * percentage;
}
function getTypwriterSpeed(speedOrAnim, maybeDuration) {
  const isObj          = speedOrAnim && typeof speedOrAnim === 'object';
  const objectSpeed    = parseFloat(isObj ? speedOrAnim.speed : speedOrAnim);
  const objectDuration = parseFloat(isObj ? speedOrAnim.duration : maybeDuration)
      || parseFloat(isObj ? speedOrAnim.parentPageDuration : 0)
      || 5;
  let percentage       = 20;
  if (Number.isNaN(objectSpeed) || objectSpeed <= 20) {
    percentage = 90;
  } else if (objectSpeed <= 40) {
    percentage = 65;
  } else if (objectSpeed <= 60) {
    percentage = 45;
  } else if (objectSpeed <= 80) {
    percentage = 28;
  } else {
    percentage = 12;
  }
  return (objectDuration / 100) * percentage;
}

function getCharacterDelay(speedOrAnim, letterCount, opts = {}) {
  const isObj     = speedOrAnim && typeof speedOrAnim === 'object';
  const speed     = isObj ? speedOrAnim.speed : speedOrAnim;
  const duration  = isObj
      ? (speedOrAnim.duration || speedOrAnim.parentPageDuration)
      : opts.duration;
  const n         = Math.max(1, letterCount);
  const onEnter   = opts.onEnter !== false;
  const onExit    = !!opts.onExit;
  const enterDur  = opts.enterDur || 0.08;
  const exitDur   = opts.exitDur || 0.08;
  const pageDur   = Math.max(0.2, parseFloat(duration) || 5);
  const budget    = Math.min(pageDur, getTypwriterSpeed(speed, pageDur));
  let letterDelay = 0;
  let hold        = 0;

  if (n <= 1) {
    hold = onEnter && onExit ? Math.max(enterDur + 0.2, budget - exitDur) : 0;
    return { letterDelay, hold };
  }

  if (onEnter && onExit) {
    const usable = Math.max(0.05, budget - enterDur - exitDur);
    letterDelay  = usable / (2 * (n - 1));
    hold         = budget - ((n - 1) * letterDelay) - exitDur;
    const lastEnterEnd = ((n - 1) * letterDelay) + enterDur;
    if (hold < lastEnterEnd) {
      hold = lastEnterEnd;
    }
  } else {
    const animDur = onExit && !onEnter ? exitDur : enterDur;
    letterDelay   = Math.max(0, (budget - animDur) / (n - 1));
  }
  return { letterDelay, hold };
}
function getNewTransitionsTime(transitionObject) { // specifically for the transitions only, not for the animations
  const max         = Math.min(5, Math.min(transitionObject.firstScreenDuration / 2, transitionObject.secondScreenDuration / 2));
  const range       = max - 0.1;
  const objectSpeed = transitionObject.speed;
  if (objectSpeed <= 20) {
    return range / 5;
  }
  if (objectSpeed <= 40) {
    return range / 4;
  }
  if (objectSpeed <= 60) {
    return range / 3;
  }
  if (objectSpeed <= 80) {
    return range / 2;
  }
  if (objectSpeed <= 100) {
    return range;
  }
}

function buildBurstKeyframes(animation, hold = 0) {
  const onEnter   = !!animation.onEnter;
  const onExit    = !!animation.onExit;
  const popIn     = 0.18;
  const popSettle = 0.32;
  const popPeak   = 0.14;
  const popOut    = 0.36;
  const origin    = '50% 50%';
  const frames    = {};
  if (onEnter) {
    frames[0]         = { opacity: 0, display: 'inline-block', transformOrigin: origin, transform: { scale: '0.1, 0.1' } };
    frames[popIn]     = { opacity: 1, display: 'inline-block', transformOrigin: origin, transform: { scale: '1.35, 1.35' } };
    frames[popSettle] = { opacity: 1, display: 'inline-block', transformOrigin: origin, transform: { scale: '1, 1' } };
  } else {
    frames[0] = { opacity: 1, display: 'inline-block', transformOrigin: origin, transform: { scale: '1, 1' } };
  }
  if (onExit) {
    const exitAt = onEnter ? hold : 0;
    if (onEnter) {
      frames[exitAt] = { opacity: 1, display: 'inline-block', transformOrigin: origin, transform: { scale: '1, 1' } };
    }
    frames[exitAt + popPeak] = { opacity: 1, display: 'inline-block', transformOrigin: origin, transform: { scale: '1.35, 1.35' } };
    frames[exitAt + popOut]  = { opacity: 0, display: 'inline-block', transformOrigin: origin, transform: { scale: '0.1, 0.1' } };
  }
  return frames;
}

function getBurstAnimation(animation, i, data) {
  const frames = buildBurstKeyframes(animation, data.hold);
  frames.options = { delay: i * data.letterDelay };
  return frames;
}

function getAnimationTransformOrigin(type) {
  return (type === 'table' || type === 'image') ? 'top left' : 'center center';
}

function getZoomStompTransform(type, scaleMultiplier, rotateDeg, width, height, restScaleX, restScaleY) {
  const restSx = Number(restScaleX) || 1;
  const restSy = Number(restScaleY) || 1;
  const m      = Number(scaleMultiplier);
  const animSx = restSx * m;
  const animSy = restSy * m;
  const w      = Number(width) || 0;
  const h      = Number(height) || 0;
  const shift = type === 'table' || type === 'image';
  const tx    = shift ? (w * (restSx - animSx)) / 2 : 0;
  const ty    = shift ? (h * (restSy - animSy)) / 2 : 0;
  return {
    translateX: `${tx}px`,
    translateY: `${ty}px`,
    rotate:     `${rotateDeg || 0}deg`,
    scale:      `${animSx}, ${animSy}`,
  };
}


function measureBurstCharacterMarks(root) {
  const rootRect = root.getBoundingClientRect();
  const sx       = rootRect.width / (root.offsetWidth || rootRect.width || 1) || 1;
  const sy       = rootRect.height / (root.offsetHeight || rootRect.height || 1) || 1;
  const marks    = [];
  const range    = document.createRange();
  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (!text || /^\s+$/.test(text)) {
        return;
      }
      let offset = 0;
      [...text].forEach((c) => {
        const len = c.length;
        range.setStart(node, offset);
        range.setEnd(node, offset + len);
        const r = range.getBoundingClientRect();
        marks.push({
          left: (r.left - rootRect.left) / sx,
          top:  (r.top - rootRect.top) / sy,
        });
        offset += len;
      });
      return;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(walk);
    }
  }
  walk(root);
  return marks;
}

function wrapBurstOnCurveLetters(root) {
  if (root.querySelector('.animationItem')) {
    return;
  }
  Array.from(root.querySelectorAll('span')).forEach((letter) => {
    if (letter.classList.contains('animationItem') || letter.querySelector('.animationItem')) {
      return;
    }
    const pos = letter.style.position || window.getComputedStyle(letter).position;
    if (pos !== 'absolute') {
      return;
    }
    const inner = document.createElement('span');
    inner.className             = 'animationItem';
    inner.style.display         = 'inline-block';
    inner.style.transformOrigin = '50% 50%';
    inner.style.color           = 'inherit';
    while (letter.firstChild) {
      inner.appendChild(letter.firstChild);
    }
    letter.appendChild(inner);
  });
}

function isCurveBurstRoot(root) {
  if (root.closest && root.closest('.curve-text-div')) {
    return true;
  }
  const span = root.querySelector('span');
  return !!(span && (span.style.position === 'absolute'
      || window.getComputedStyle(span).position === 'absolute'));
}

function isBurstRootLaidOut(el) {
  if (!el) {
    return false;
  }
  const r = el.getBoundingClientRect();
  return r.width > 1 && r.height > 1;
}

function getBurstMeasureRoot(root) {
  if (isBurstRootLaidOut(root)) {
    return root;
  }
  const host = root.closest && (root.closest('[data-type="text"]') || root.closest('.text-object'));
  if (!host || !host.id) {
    return root;
  }
  const twin = document.querySelector(`.canvas-non-play-all [id='${host.id}']`);
  if (!twin || twin === host || (twin.contains && twin.contains(root))) {
    return root;
  }
  const twinRoot = root.classList && root.classList.contains('focus-text-div')
      ? (twin.querySelector('.focus-text-div') || twin)
      : twin;
  return isBurstRootLaidOut(twinRoot) ? twinRoot : root;
}

function applyBurstGradientToLetters(root, items, marks, width, height) {
  const rootStyle = window.getComputedStyle(root);
  const image     = rootStyle.backgroundImage;
  if (rootStyle.webkitBackgroundClip !== 'text' || !image || image === 'none') {
    return;
  }
  const bleedY = parseFloat(rootStyle.paddingTop) || 0;
  const bleedX = parseFloat(rootStyle.paddingLeft) || 0;
  root.style.backgroundImage = 'none';
  items.forEach((item, i) => {
    item.style.boxSizing            = 'content-box';
    item.style.padding              = `${bleedY}px ${bleedX}px`;
    item.style.margin               = `${-bleedY}px ${-bleedX}px`;
    item.style.backgroundImage      = image;
    item.style.backgroundOrigin     = 'padding-box';
    item.style.backgroundSize       = `${width}px ${height}px`;
    item.style.backgroundRepeat     = 'no-repeat';
    item.style.backgroundPosition   = `${bleedX - marks[i].left}px ${bleedY - marks[i].top}px`;
    item.style.webkitBackgroundClip = 'text';
    item.style.backgroundClip       = 'text';
    item.style.webkitTextFillColor  = 'transparent';
  });
}
function wrapBurstText(root) {
  if (!root) {
    return;
  }
  if (isCurveBurstRoot(root) && root.querySelector('span')) {
    wrapBurstOnCurveLetters(root);
    if (root.querySelector('.animationItem')) {
      return;
    }
  }
  const source     = getBurstMeasureRoot(root);
  const holdWidth  = source.offsetWidth || root.offsetWidth;
  const holdHeight = source.offsetHeight || root.offsetHeight;
  const marks      = measureBurstCharacterMarks(source);
  spanEachCharacter(root);
  const items = root.querySelectorAll('.animationItem');
  if (!marks.length || items.length !== marks.length) {
    return;
  }
  applyBurstGradientToLetters(root, items, marks, holdWidth, holdHeight);
  const hasSpread = marks.some((m, i) => i > 0 && (Math.abs(m.left - marks[0].left) > 0.5 || Math.abs(m.top - marks[0].top) > 0.5));
  if (!hasSpread) {
    return;
  }
  const laidOut = window.getComputedStyle(root);
  if (laidOut.position === 'static') {
    root.style.position = 'relative';
  }
  const padX = (parseFloat(laidOut.paddingLeft) || 0) + (parseFloat(laidOut.paddingRight) || 0);
  const padY = (parseFloat(laidOut.paddingTop) || 0) + (parseFloat(laidOut.paddingBottom) || 0);
  const borderBox = laidOut.boxSizing === 'border-box';
  root.style.width     = `${borderBox ? holdWidth : Math.max(0, holdWidth - padX)}px`;
  root.style.minWidth  = root.style.width;
  root.style.minHeight = `${borderBox ? holdHeight : Math.max(0, holdHeight - padY)}px`;
  items.forEach((item, i) => {
    item.style.position        = 'absolute';
    item.style.left            = `${marks[i].left}px`;
    item.style.top             = `${marks[i].top}px`;
    if (!item.style.padding) {
      item.style.margin = '0';
    }
    item.style.display         = 'inline-block';
    item.style.transformOrigin = '50% 50%';
  });
}

function spanEachCharacter(el) {
  if (!el) {
    return;
  }
  if (el.nodeType == Node.TEXT_NODE) {
    const text = el.textContent;
    if (!text || /^\s+$/.test(text)) {
      return;
    }
    let html = '';
    [...text].forEach((c) => {
      html += `<span class="animationItem" style="display:inline-block;transform-origin:50% 50%;">${c}</span>`;
    });

    const node = document.createElement('span');
    el.parentNode.insertBefore(node, el);
    el.parentNode.removeChild(el);
    node.outerHTML = html;
  } else if (el.nodeType === Node.ELEMENT_NODE) {
    Array.from(el.childNodes).forEach((child) => {
      spanEachCharacter(child);
    });
  }
}

function characterWrapping(selector) {
  let newHTML          = '';
  const originalHTML   = document.querySelector(selector).innerHTML;
  let tagStarted       = false;
  let ampersendStarted = false;
  for (let i = 0; i < originalHTML.length; i += 1) { // lettering
    if (originalHTML[i] === '<' && !ampersendStarted) {
      tagStarted = true;
    }
    if (originalHTML[i] === '&' && !tagStarted) {
      ampersendStarted = true;
      newHTML          = `${newHTML}<span class="animationItem" >`;
    }
    if (!tagStarted && !ampersendStarted) {
      newHTML = `${newHTML}<span class="animationItem" >${originalHTML[i]}</span>`;
    } else {
      newHTML += originalHTML[i];
    }
    if (originalHTML[i] === '>' && tagStarted) {
      tagStarted = false;
    }
    if (originalHTML[i] === ';' && ampersendStarted) {
      ampersendStarted = false;
      newHTML          = `${newHTML}</span>`;
    }
  }
  document.querySelector(selector).innerHTML = newHTML;
}
function getScale(type, scaleX, scaleY) {
  return ['text', 'table'].includes(type)
      ? `${Number(scaleX) || 1},${Number(scaleY) || 1}`
      : undefined;
}
export function panleft({
                          animation, type, scaleX, scaleY, RotateDegree, objectOpacity,
                        }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const distance         = parseFloat(animation.distance) + 20;
  const animationSteps   = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: `${distance}%`,
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }

  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: `-${distance}%`,
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - 0.000001] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]            = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function panright({
                           animation, type, scaleX, scaleY, RotateDegree, objectOpacity,
                         }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const distance         = parseFloat(animation.distance) + 20;
  const animationSteps   = {};
  animationSteps[0.0]    = {};
  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: `-${distance}%`,
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }

  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateX: `${distance}%`,
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - 0.0001] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]          = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function pantop({
                         animation, type, scaleX, scaleY, RotateDegree, objectOpacity,
                       }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const distance         = parseFloat(animation.distance) + 20;

  const animationSteps = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: `${distance}%`,
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }

  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: `-${distance}%`,
        rotate:     `${RotateDegree}deg`,
      },
      opacity: 1,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - 0.0001] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]          = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function panbottom({
                            animation, type, scaleX, scaleY, RotateDegree, objectOpacity,
                          }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const distance         = parseFloat(animation.distance) + 20;

  const animationSteps = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: `-${distance}%`,
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }

  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      transform: {
        scale:      getScale(type, scaleX, scaleY),
        translateY: `${distance}%`,
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - 0.0001] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]          = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function zoomin({
                         animation, type, scaleX, scaleY, RotateDegree, objectOpacity, Width, Height,
                       }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const sx               = Number(scaleX) || 1;
  const sy               = Number(scaleY) || 1;
  const transformOrigin  = getAnimationTransformOrigin(type);
  const zoomTf           = multiplier => getZoomStompTransform(
      type, multiplier, RotateDegree, Width, Height, sx, sy,
  );

  const animationSteps = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transformOrigin,
      transform: zoomTf(0.7),
      opacity:   0,
    };
    animationSteps[start + transitionDuration] = {
      transformOrigin,
      transform: zoomTf(1),
      opacity:   objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transformOrigin,
      transform: zoomTf(1),
      opacity:   objectOpacity,
    };
  }

  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      transformOrigin,
      transform: zoomTf(1),
      opacity:   objectOpacity,
    };
    animationSteps[start + duration]                      = {
      transformOrigin,
      transform: zoomTf(1.3),
      opacity:   0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - 0.0001] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]          = {
      opacity: 0,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function zoomout({
                          animation, objectOpacity,
                        }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);

  const animationSteps = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transformOrigin: 'center center',
      transform:       {
        scale: '130%',
      },
      opacity: 0,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        scale: '100%',
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transformOrigin: 'center center',
      transform:       {
        scale: '100%',
      },
      opacity: objectOpacity,
    };
  }

  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      transformOrigin: 'center center',
      transform:       {
        scale: '100%',
      },
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      transformOrigin: 'center center',
      transform:       {
        scale: '130%',
      },
      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - 0.0001] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]          = {
      opacity: 0,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function stomp({
                        animation, type, scaleX, scaleY, RotateDegree, objectOpacity, Width, Height,
                      }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const sx               = Number(scaleX) || 1;
  const sy               = Number(scaleY) || 1;
  const transformOrigin  = getAnimationTransformOrigin(type);
  const zoomTf           = multiplier => getZoomStompTransform(
      type, multiplier, RotateDegree, Width, Height, sx, sy,
  );

  const animationSteps = {};

  if (animation.onEnter) {
    animationSteps[start]                               = {
      transformOrigin,
      transform: zoomTf(10),
      opacity:   objectOpacity,
    };
    animationSteps[start + transitionDuration]          = {
      transformOrigin,
      transform: zoomTf(1),
      opacity:   objectOpacity,
    };
    animationSteps[start + transitionDuration + 0.0001] = {
      transformOrigin,
      transform: zoomTf(1),
      opacity:   objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transformOrigin,
      transform: zoomTf(1),
      opacity:   objectOpacity,
    };
  }

  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      // transform: {
      //   scale: (ImageType == 'Image' || type == 'video' || ImageType == 'SVGImage') ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
      // },
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      // transform: {
      //   scale: '1000%',
      // },
      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - 0.0001] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]          = {
      opacity: 0,
    };
  }
  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function fade({
                       animation, objectOpacity,
                     }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);

  const animationSteps = {};

  if (animation.onEnter) {
    animationSteps[start]                               = {
      opacity: 0,
    };
    animationSteps[start + transitionDuration]          = {
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration + 0.0001] = {
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      opacity: objectOpacity,
    };
  }

  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - 0.0001] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]          = {
      opacity: 0,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function photoZomEnterIn({
                                  animation, RotateDegree, objectOpacity, ImageType, IsHFlipped, IsVFlipped,
                                }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const animationSteps   = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: 0,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-130%' : '130%'},${IsVFlipped === true ? '-130%' : '130%'}` : '130%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {

      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {

      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  return item;
}

export function photoZomEnterOut({
                                   animation, RotateDegree, objectOpacity, ImageType, IsHFlipped, IsVFlipped,
                                 }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const animationSteps   = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-120%' : '120%'},${IsVFlipped === true ? '-120%' : '120%'}` : '120%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: 0,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  return item;
}

export function photoRiseTop({
                               animation, ImageType, IsVFlipped, RotateDegree, objectOpacity, IsHFlipped,
                             }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const animationSteps   = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:      ImageType == 'Image' ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
        translateY: ImageType == 'Image' ? `${IsVFlipped === true ? '0%' : '0%'}` : '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: 0,
    };
    animationSteps[start + 0.0001]             = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-120%' : '120%'},${IsVFlipped === true ? '-120%' : '120%'}` : '120%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        translateY: ImageType == 'Image' ? `${IsVFlipped === true ? '5%' : '-5%'}` : '-5%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:      ImageType == 'Image' ? `${IsHFlipped === true ? -1 : 1},${IsVFlipped === true ? -1 : 1}` : undefined,
        translateY: ImageType == 'Image' ? `${IsVFlipped === true ? '0%' : '0%'}` : '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  return item;
}

export function photoRiseBottom({
                                  animation, RotateDegree, objectOpacity, ImageType, IsHFlipped, IsVFlipped,
                                }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const animationSteps   = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:      ImageType == 'Image' ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
        translateY: ImageType == 'Image' ? `${IsVFlipped === true ? '0%' : '0%'}` : '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: 0,
    };
    animationSteps[start + 0.0001]             = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-120%' : '120%'},${IsVFlipped === true ? '-120%' : '120%'}` : '120%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        translateY: ImageType == 'Image' ? `${IsVFlipped === true ? '-5%' : '5%'}` : '5%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:      ImageType == 'Image' ? `${IsHFlipped === true ? -1 : 1},${IsVFlipped === true ? -1 : 1}` : undefined,
        translateY: ImageType == 'Image' ? `${IsVFlipped === true ? '0%' : '0%'}` : '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  return item;
}

export function photoRiseLeft({
                                animation, RotateDegree, objectOpacity, ImageType, IsHFlipped, IsVFlipped,
                              }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);
  const animationSteps   = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:      ImageType == 'Image' ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
        translateX: ImageType == 'Image' ? `${IsVFlipped === true ? '0%' : '0%'}` : '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: 0,
    };
    animationSteps[start + 0.0001]             = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-120%' : '120%'},${IsVFlipped === true ? '-120%' : '120%'}` : '120%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        translateX: ImageType == 'Image' ? `${IsHFlipped === true ? '5%' : '-5%'}` : '-5%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:      ImageType == 'Image' ? `${IsHFlipped === true ? -1 : 1},${IsVFlipped === true ? -1 : 1}` : undefined,
        translateX: ImageType == 'Image' ? `${IsVFlipped === true ? '0%' : '0%'}` : '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  return item;
}

export function photoRiseRight({
                                 animation, RotateDegree, objectOpacity, ImageType, IsHFlipped, IsVFlipped,
                               }) {
  const start            = parseFloat(animation.startTime);
  const speed            = parseFloat(animation.speed);
  const duration         = parseFloat(animation.duration);
  let transitionDuration = 0.0;
  transitionDuration     = getTransitionTime(speed, duration);

  const animationSteps = {};

  if (animation.onEnter) {
    animationSteps[start]                      = {
      transform: {
        scale:      ImageType == 'Image' ? `${IsHFlipped === true ? '-100%' : '100%'},${IsVFlipped === true ? '-100%' : '100%'}` : '100%',
        translateX: ImageType == 'Image' ? `${IsVFlipped === true ? '0%' : '0%'}` : '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: 0,
    };
    animationSteps[start + 0.0001]             = {
      transform: {
        scale:  ImageType == 'Image' ? `${IsHFlipped === true ? '-120%' : '120%'},${IsVFlipped === true ? '-120%' : '120%'}` : '120%',
        rotate: `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
    animationSteps[start + transitionDuration] = {
      transform: {
        translateX: ImageType == 'Image' ? `${IsHFlipped === true ? '-5%' : '5%'}` : '5%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (!animation.onEnter) {
    animationSteps[start] = {
      transform: {
        scale:      ImageType == 'Image' ? `${IsHFlipped === true ? -1 : 1},${IsVFlipped === true ? -1 : 1}` : undefined,
        translateX: ImageType == 'Image' ? `${IsVFlipped === true ? '0%' : '0%'}` : '0%',
        rotate:     `${RotateDegree}deg`,
      },
      opacity: objectOpacity,
    };
  }
  if (animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
    animationSteps[start + duration]                      = {
      opacity: 0,
    };
  }
  if (!animation.onExit) {
    animationSteps[start + duration - transitionDuration] = {
      opacity: objectOpacity,
    };
  }

  const item = new Scene({
    [animation.selector]: animationSteps,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });

  return item;
}

export function typewriter({
                             animation,
                           }) {
  const start        = parseFloat(animation.startTime);
  const { selector } = animation;
  if (document.querySelectorAll(`${selector} .animationItem`).length == 0) {
    characterWrapping(selector);
  }

  const letterCount = document.querySelectorAll(`${selector} .animationItem`).length;
  const onEnter     = !!animation.onEnter;
  const onExit      = !!animation.onExit;
  const timing      = getCharacterDelay(animation, letterCount, {
    onEnter,
    onExit,
    enterDur: 0.08,
    exitDur:  0.08,
  });
  const appear = 0.08;
  const hold   = timing.hold;
  const frames = {};
  if (onEnter) {
    frames[0]      = { visibility: 'hidden' };
    frames[appear] = { visibility: 'visible' };
  } else {
    frames[0] = { visibility: 'visible' };
  }
  if (onExit) {
    const exitAt = onEnter ? hold : 0;
    if (onEnter) {
      frames[exitAt] = { visibility: 'visible' };
    }
    frames[exitAt + appear] = { visibility: 'hidden' };
  }

  const item = new Scene({
    [`${selector} .animationItem`]: i => ({
      ...frames,
      options: {
        delay: i * timing.letterDelay,
      },
    }),
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  item.setDelay(start);
  return item;
}


export function burst({ animation }) {
  const start        = parseFloat(animation.startTime);
  const { selector } = animation;
  if (document.querySelectorAll(`${selector} .animationItem`).length == 0) {
    const videoSelector = `.canvas-video-wrapper ${selector.replace('.canvas-section ', '')}`;
    const el            = document.querySelector(videoSelector) || document.querySelector(selector);
    if (el) {
      wrapBurstText(el.querySelector('.focus-text-div') || el);
    }
  }
  const numOfCharacter = [document.querySelectorAll(`${selector} .animationItem`)][0].length;
  const timing         = getCharacterDelay(animation, numOfCharacter, {
    onEnter:  !!animation.onEnter,
    onExit:   !!animation.onExit,
    enterDur: 0.32,
    exitDur:  0.36,
  });

  const item = new Scene({
    [`${selector} .animationItem`]: i => getBurstAnimation(animation, i, {
      numOfCharacter,
      letterDelay: timing.letterDelay,
      hold:        timing.hold,
    }),
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  item.setDelay(start);
  return item;
}

export function transitionPanLeft(tranition, index) {
  const trScene           = new Scene();
  const tansitionDuration = getNewTransitionsTime(tranition);

  if (index == 0) {
    const ani       = {
      0: {
        left: 0,
      },
    };
    const tempScene = new Scene({
      '.page-0': ani,
    });
    trScene.setItem('page-0', tempScene);
  }

  const a1                  = {
    0: {
      'z-index': 6 - index,
    },
    [tranition.secondScreenStartTime - tansitionDuration - 0.001]: {
      left: '0',
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      left: '0',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      left: '-100%',
    },
  };
  const a2                  = {
    0: {
      left: '100%',
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      left: '100%',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      left: '0',
    },
  };
  const page1               = `.page-${index}`;
  const page2               = `.page-${index + 1}`;
  const transitionPageScene = new Scene({
    [page1]: a1,
    [page2]: a2,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  trScene.setItem(`transition${index}`, transitionPageScene);


  return trScene;
}

export function transitionPanRight(tranition, index) {
  const trScene           = new Scene();
  const tansitionDuration = getNewTransitionsTime(tranition);

  if (index == 0) {
    const ani       = {
      0: {
        left: 0,
      },
    };
    const tempScene = new Scene({
      '.page-0': ani,
    });
    trScene.setItem('page-0', tempScene);
  }

  const a1                  = {
    0: {
      'z-index': 6 - index,
    },
    [tranition.secondScreenStartTime - tansitionDuration - 0.001]: {
      left: '0',
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      left: '0',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      left: '100%',
    },
  };
  const a2                  = {
    0: {
      left: '-100%',
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      left: '-100%',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      left: '0',
    },
  };
  const page1               = `.page-${index}`;
  const page2               = `.page-${index + 1}`;
  const transitionPageScene = new Scene({
    [page1]: a1,
    [page2]: a2,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  trScene.setItem(`transition${index}`, transitionPageScene);


  return trScene;
}

export function transitionPanTop(tranition, index) {
  const trScene           = new Scene();
  const tansitionDuration = getNewTransitionsTime(tranition);

  if (index == 0) {
    const ani       = {
      0: {
        top:  0,
        left: 0,
      },
    };
    const tempScene = new Scene({
      '.page-0': ani,
    });
    trScene.setItem('page-0', tempScene);
  }

  const a1                  = {
    0: {
      'z-index': 6 - index,
      left:      0,
    },
    [tranition.secondScreenStartTime - tansitionDuration - 0.001]: {
      top: '0',
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      top: '0',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      top: '-100%',
    },
  };
  const a2                  = {
    0: {
      top:  '100%',
      left: 0,
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      top: '100%',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      top: '0',
    },
  };
  const page1               = `.page-${index}`;
  const page2               = `.page-${index + 1}`;
  const transitionPageScene = new Scene({
    [page1]: a1,
    [page2]: a2,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  trScene.setItem(`transition${index}`, transitionPageScene);


  return trScene;
}

export function transitionPanBottom(tranition, index) {
  const trScene           = new Scene();
  const tansitionDuration = getNewTransitionsTime(tranition);

  if (index == 0) {
    const ani       = {
      0: {
        top:  0,
        left: 0,
      },
    };
    const tempScene = new Scene({
      '.page-0': ani,
    });
    trScene.setItem('page-0', tempScene);
  }

  const a1                  = {
    0: {
      'z-index': 6 - index,
      left:      0,
    },
    [tranition.secondScreenStartTime - tansitionDuration - 0.001]: {
      top: '0',
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      top: '0',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      top: '100%',
    },
  };
  const a2                  = {
    0: {
      top:  '-100%',
      left: 0,
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      top: '-100%',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      top: '0',
    },
  };
  const page1               = `.page-${index}`;
  const page2               = `.page-${index + 1}`;
  const transitionPageScene = new Scene({
    [page1]: a1,
    [page2]: a2,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  trScene.setItem(`transition${index}`, transitionPageScene);


  return trScene;
}


export function transitionFade(tranition, index) {
  const trScene           = new Scene();
  const tansitionDuration = getNewTransitionsTime(tranition);

  if (index == 0) {
    const ani       = {
      0: {
        opacity:   1,
        left:      0,
        transform: 'translate(0)',
      },
    };
    const tempScene = new Scene({
      '.page-0': ani,
    });
    trScene.setItem('page-0', tempScene);
  }

  const a1                  = {
    0: {
      'z-index': 6 - index,
      left:      0,
      transform: 'translate(0)',
    },
    [tranition.secondScreenStartTime - tansitionDuration - 0.001]: {
      opacity: '1',
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      opacity: '1',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      opacity: '0',
    },
  };
  const a2                  = {
    0: {
      opacity:   '1',
      left:      0,
      transform: 'translate(0)',
    },
    [tranition.secondScreenStartTime - tansitionDuration - 0.0001]: {
      opacity: '1',
    },
    [tranition.secondScreenStartTime - tansitionDuration]: {
      opacity: '1',
    },
    [tranition.secondScreenStartTime + tansitionDuration]: {
      opacity: '1',
    },
  };
  const page1               = `.page-${index}`;
  const page2               = `.page-${index + 1}`;
  const transitionPageScene = new Scene({
    [page1]: a1,
    [page2]: a2,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  trScene.setItem(`transition${index}`, transitionPageScene);


  return trScene;
}

export function transitionNone(tranition, index) {
  const trScene = new Scene();

  if (index == 0) {
    const ani       = {
      0: {
        opacity:   1,
        'z-index': 6 - index,
        left:      0,
        transform: 'translate(0)',
      },
      0.001: {
        opacity:   1,
        'z-index': 6 - index,
      },
      [tranition.secondScreenStartTime - 0.001]: {
        opacity:   1,
        'z-index': 6 - index,
      },
    };
    const tempScene = new Scene({
      '.page-0': ani,
    });
    trScene.setItem('page-0', tempScene);
  }

  const a1                  = {
    0: {
      'z-index': 6 - index,
      opacity:   1,
      left:      0,
      transform: 'translate(0)',
    },

    [tranition.firstScreenStartTime]: {
      opacity:   '1',
      'z-index': 6 - index,
      left:      0,
    },
    [tranition.firstScreenStartTime + 0.001]: {
      opacity:   '1',
      'z-index': 6 - index,
      left:      0,
    },
    [tranition.firstScreenStartTime + tranition.firstScreenDuration - 0.001]: {
      opacity:   '1',
      'z-index': 6 - index,
      left:      0,
    },
    [tranition.firstScreenStartTime + tranition.firstScreenDuration]: {
      opacity:   '0',
      'z-index': 6 - index,
      left:      0,

    },
  };
  const a2                  = {
    0: {
      opacity:   '0',
      left:      0,
      transform: 'translate(0)',
      'z-index': 6 - index,
    },
    [tranition.secondScreenStartTime - 0.001]: {
      opacity:   '0',
      'z-index': 6 - index,
    },
    [tranition.secondScreenStartTime]: {
      opacity:   '1',
      'z-index': 6 - index,
    },
    [tranition.secondScreenStartTime + 0.001]: {
      opacity:   '1',
      'z-index': 6 - index,
    },
    [tranition.secondScreenStartTime + tranition.secondScreenDuration]: {
      opacity:   '1',
      'z-index': 6 - index,
    },
  };
  const page1               = `.page-${index}`;
  const page2               = `.page-${index + 1}`;
  const transitionPageScene = new Scene({
    [page1]: a1,
    [page2]: a2,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  trScene.setItem(`transition${index}`, transitionPageScene);
  console.log('trScene');
  console.log(trScene);
  return trScene;
}

export function singlePageDisplay(animation) {
  const ani                 = {
    0: {
      opacity: 1,
    },
    [animation.duration]: {
      opacity: 1,
      left:    0,
    },
  };
  const transitionPageScene = new Scene({
    '.page-0': ani,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  return transitionPageScene;
}

export function lastPageDisplay(transition, index) {
  const ani                 = {
    [transition.secondScreenStartTime - 0.001]: {
      opacity: 0,
    },
    [transition.secondScreenStartTime - 0.001]: {
      opacity: 1,
    },
    [transition.secondScreenStartTime + transition.secondScreenDuration]: {
      opacity: 1,
    },
    [transition.secondScreenStartTime + transition.secondScreenDuration + 0.001]: {
      opacity: 0,
    },
  };
  const transitionPageScene = new Scene({
    [`.page-${index}`]: ani,
  }, {
    easing:   'ease-in-out',
    selector: true,
  });
  return transitionPageScene;
}
